// Procedural pixel visitors. Pure: draws through a rect painter R(x, y, w, h, colour).
// The same seed always gives the same person. Before the reveal, aliens and humans look the same.
import { dz as dither, T as TN } from './bits.js';
import { costumeOf } from '../game/costumes.js';
import { headOf, failFor } from '../game/cast.js';
import { drawnAnimal, headFrame, headArt } from './animal-art.js';
import { eyeStamps } from './eyes.js';
import { knockerSheet, paintKnocker } from './knocker.js';
import { paintScream, paintFlood, paintCracks, paintCreep, paintStamp } from './red.js';
import { ART, LEAF, glassArt } from './night.js';

export { seedOf } from '../game/cast.js'; // the look's seed, shared with the server so it knows who was met
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const pick = (r, list) => list[Math.floor(r() * list.length)];
// murmur3's finaliser: spreads nearby seeds apart (the first rng() draw for knock-0, knock-1, ... clusters)
const fmix = h => { h >>>= 0; h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16; return h >>> 0; };

// [base, shade, deep]
const SKINS = [
  ['#f2cfb3', '#c99a7a', '#a9785c'], ['#e0ae86', '#b9845f', '#8f6346'], ['#c48a5e', '#9a6844', '#74492e'],
  ['#8d5a3b', '#6b412a', '#4a2a1a'], ['#5e3a26', '#452a1b', '#2e1b10'], ['#ead8cc', '#bfa699', '#958076'],
];
const INFECTED = ['#9fb08a', '#6f7d5c', '#4e5a40'];
const HAIR = ['#141010', '#2e1d12', '#5a3a1e', '#8a5a2a', '#c9a050', '#d8d0c0', '#8a8a8a', '#8c2f1c', '#3b2a4a'];
const COATS = ['#2a2433', '#24402f', '#3a2a3a', '#2c3550', '#40302a', '#4a4a52', '#5a2a26', '#2f4a4a', '#6b5a3a', '#1f1f24'];
const ACCENTS = ['#c2577a', '#f0a640', '#c9b98e', '#4f6e5f', '#8c2f2a', '#6f86ff', '#e9dcb8', '#7a4fa0'];
const MASKS = ['#dfe6e8', '#a9c7d8', '#2a2a30', '#c9b98e'];
const STYLES = ['short', 'long', 'bun', 'curly', 'bald', 'beanie', 'cap', 'hood', 'bob', 'ponytail', 'mohawk', 'hat'];
const EXPRESSIONS = ['neutral', 'smile', 'worried', 'tired', 'smirk'];
const INK = '#1a1410', EYE_SHINE = '#f5f0e6', GOLD = '#f0c040', PINK = '#d0607a', SORE = '#8c2f2a', BLOOD = '#c0392b';

export function makeLook(seed, { outbreak = false, halloween = false, animal = false, degen = false } = {}) {
  const r = rng(seed);
  pick(r, STYLES); // still drawn, so every other trait keeps the value it had
  // headwear or hair from a well-mixed hash: 4 of the 12 are headwear, so about a third of visitors wear one
  const hairStyle = STYLES[fmix(seed ^ 0x165667b1) % STYLES.length];
  const look = {
    skin: pick(r, SKINS),
    hairStyle,
    hairColor: pick(r, HAIR),
    coat: pick(r, COATS),
    accent: pick(r, ACCENTS),
    scarf: r() < 0.55,
    glasses: r() < 0.3,
    beard: r() < 0.22,
    mustache: r() < 0.15,
    earrings: r() < 0.25,
    freckles: r() < 0.2,
    expression: pick(r, EXPRESSIONS),
    headW: 9 + Math.floor(r() * 3),
    headH: 11 + Math.floor(r() * 3),
    bodyW: 20 + Math.floor(r() * 6),
    height: Math.floor(r() * 7) - 3,
    alienFace: pick(r, ['wells', 'wide', 'third']),
    sores: Array.from({ length: 5 }, () => [Math.floor(r() * 15) - 7, Math.floor(r() * 18) - 8]),
    mask: false,
    maskColor: pick(r, MASKS),
  };
  if (outbreak) look.mask = r() < 0.45;
  // Halloween: art only, drawn over the visitor. costumeOf never sees who is an alien and draws nothing from r.
  if (halloween) look.costume = costumeOf(seed, { halloween, outbreak });
  // animal: a Full fever door, an animal instead of a person (and instead of a costume). degen: the normal lineup, where the mixed-in
  // animals, the degen visitors and the drawn neighbours wear the same kind of drawn head, from the seed alone (game/cast.js headOf)
  const kind = headOf(seed, { animal, degen });
  if (kind) { look.animal = kind; look.costume = null; }
  // the wrong-Open scream's monster: the Knocker, or now and then one of its stand-ins; a calm neighbour screams as its own
  const fail = failFor(seed, look.animal);
  if (fail) look.fail = fail;
  return look;
}

// mode: { reveal: null | 'human' | 'alien' | 'infected' }
export function drawVisitor(R, cx, cy, look, t, mode = {}) {
  const reveal = mode.reveal ?? null;
  const infected = reveal === 'infected', alien = reveal === 'alien';
  const skin = infected ? INFECTED : look.skin;
  const fy = cy + 8 + Math.round(Math.sin(t / 18)) + look.height;
  const hx = cx, hcy = fy - 6, hw = look.headW, hh = look.headH + (alien && look.alienFace === 'wells' ? 3 : 0);
  const top = hcy - hh;
  const style = look.hairStyle, hair = look.hairColor;

  // --- behind the head: hood, long hair ---
  if (style === 'hood') for (let dy = -hh - 4; dy <= hh + 6; dy++) {
    const w = Math.round((hw + 5) * Math.sqrt(Math.max(0, 1 - (dy / (hh + 6)) ** 2)));
    R(hx - w, hcy + dy, w * 2, 1, look.coat);
  }
  if (style === 'long' || style === 'bob') {
    const bottom = hcy + (style === 'long' ? hh + 14 : hh - 2);
    for (let y = top - 1; y <= bottom; y++) {
      const dy = y - hcy;
      const skull = Math.round((hw + 2) * Math.sqrt(Math.max(0, 1 - (Math.min(dy, 0) / (hh + 1)) ** 2)));
      const taper = dy > hh ? Math.max(0, skull - Math.round((dy - hh) * 0.4)) : skull;
      R(hx - taper, y, taper * 2, 1, hair);
    }
    R(hx - hw - 2, hcy + hh - 3, 1, 1, '#00000044');
  }
  if (style === 'ponytail') { R(hx + hw - 1, top + 3, 4, 4, hair); R(hx + hw + 1, top + 6, 3, 12, hair); R(hx + hw + 2, top + 18, 2, 3, hair); }

  // --- body: sloped shoulders, coat shading, lapel ---
  for (let y = 0; y < 62; y++) {
    const half = Math.min(look.bodyW, 7 + Math.round(y * 1.7));
    R(hx - half, fy + 9 + y, half * 2, 1, look.coat);
    R(hx + Math.round(half * 0.45), fy + 9 + y, half - Math.round(half * 0.45), 1, '#00000033');
    R(hx - half, fy + 9 + y, 1, 1, '#ffffff22');
  }
  R(hx - 1, fy + 14, 1, 50, '#00000044');
  R(hx - 4, fy + 2, 8, 9, skin[1]);
  if (look.scarf) { R(hx - 9, fy + 8, 18, 5, look.accent); R(hx + 3, fy + 12, 4, 10, look.accent); R(hx - 9, fy + 12, 18, 1, '#00000033'); }

  // --- head ---
  for (let dy = -hh; dy <= hh; dy++) {
    const w = Math.round(hw * Math.sqrt(Math.max(0, 1 - (dy / hh) ** 2)));
    if (!w) continue;
    R(hx - w, hcy + dy, w * 2, 1, skin[0]);
    const s = Math.round(w * 0.35);
    R(hx + s, hcy + dy, w - s, 1, skin[1]);
  }
  R(hx - hw - 1, hcy - 1, 2, 4, skin[1]); R(hx + hw - 1, hcy - 1, 2, 4, skin[2]);
  if (look.earrings) { R(hx - hw - 1, hcy + 3, 1, 2, GOLD); R(hx + hw, hcy + 3, 1, 2, GOLD); }

  const ey = hcy - 1, eyes = [hx - 5, hx + 3], my = ey + 7;

  // --- brows, nose ---
  const tilt = { neutral: [0, 0], smile: [0, 0], worried: [1, -1], tired: [1, 1], smirk: [0, -1] }[look.expression];
  R(eyes[0] - 1, ey - 3 + tilt[0], 3, 1, hair === '#d8d0c0' ? skin[2] : hair);
  R(eyes[1], ey - 3 - tilt[1], 3, 1, hair === '#d8d0c0' ? skin[2] : hair);
  R(hx, ey + 2, 1, 3, skin[1]); R(hx + 1, ey + 4, 1, 1, skin[2]);
  if (look.freckles && !infected) for (const [fx, fyy] of [[-6, 3], [-4, 4], [4, 3], [6, 4]]) R(hx + fx, ey + fyy, 1, 1, skin[2]);

  // --- eyes ---
  if (alien) {
    for (const ex of eyes) { R(ex - 1, ey - 1, 4, 4, INK); R(ex, ey, 1, 1, PINK); }
  } else if (infected) {
    for (const ex of eyes) { R(ex - 1, ey + 2, 4, 1, skin[2]); R(ex, ey, 2, 2, '#e8d8c0'); R(ex + 1, ey, 1, 1, BLOOD); }
  } else {
    const blink = t % 140 < 4;
    for (const ex of eyes) {
      if (blink) R(ex, ey + 1, 2, 1, INK);
      else { R(ex, ey, 2, 2, INK); R(ex + 1, ey, 1, 1, EYE_SHINE); }
    }
  }

  // --- mouth (or mask) ---
  const maskOn = look.mask && !infected;
  if (maskOn) {
    for (let y = ey + 3; y <= hcy + hh - 2; y++) {
      const w = Math.round(hw * Math.sqrt(Math.max(0, 1 - ((y - hcy) / hh) ** 2)));
      R(hx - w, y, w * 2, 1, look.maskColor);
    }
    R(hx - hw, ey + 4, hw * 2, 1, '#00000022'); R(hx - hw - 1, ey + 2, 1, 3, look.maskColor); R(hx + hw, ey + 2, 1, 3, look.maskColor);
  } else if (alien && look.alienFace !== 'third') {
    R(hx - 7, my, 14, 2, INK);
  } else if (infected) {
    R(hx - 3, my, 6, 1, '#2e1b10'); R(hx + 2, my + 1, 1, 2, SORE);
    for (const [sx, sy] of look.sores) R(hx + sx, hcy + sy, 1, 1, sx % 2 ? SORE : PINK);
    R(hx + 9, my - 1, 1, 1, INFECTED[0]); R(hx + 11, my - 2, 1, 1, INFECTED[0]); // a cough
  } else {
    const m = look.expression;
    if (m === 'smile') { R(hx - 3, my, 6, 1, INK); R(hx - 4, my - 1, 1, 1, INK); R(hx + 3, my - 1, 1, 1, INK); }
    else if (m === 'worried') { R(hx - 2, my, 4, 1, INK); R(hx - 3, my + 1, 1, 1, INK); R(hx + 2, my + 1, 1, 1, INK); }
    else if (m === 'tired') R(hx - 2, my, 4, 1, skin[2]);
    else if (m === 'smirk') { R(hx - 2, my, 5, 1, INK); R(hx + 3, my - 1, 1, 1, INK); }
    else R(hx - 3, my, 6, 1, INK);
  }
  if (!maskOn && look.mustache) R(hx - 3, my - 2, 7, 1, hair);
  if (!maskOn && look.beard) for (let y = my + 1; y <= hcy + hh; y++) {
    const w = Math.round(hw * Math.sqrt(Math.max(0, 1 - ((y - hcy) / hh) ** 2)));
    R(hx - w, y, w * 2, 1, hair);
  }

  // --- glasses ---
  if (look.glasses && !alien) for (const ex of eyes.slice(0, 2)) {
    R(ex - 2, ey - 1, 5, 1, INK); R(ex - 2, ey + 2, 5, 1, INK); R(ex - 2, ey - 1, 1, 4, INK); R(ex + 2, ey - 1, 1, 4, INK);
    if (ex === eyes[0]) R(ex + 3, ey, 3, 1, INK);
  }

  // --- hair on top ---
  const cap = (h, colour, extra = 0) => {
    for (let dy = -hh - extra; dy < -hh + h; dy++) {
      const w = Math.round((hw + 1) * Math.sqrt(Math.max(0, 1 - (Math.min(dy, -hh / 2) / (hh + extra)) ** 2)));
      R(hx - w, hcy + dy, w * 2, 1, colour);
    }
  };
  switch (style) {
    case 'short': cap(5, hair); R(hx - hw, top + 4, 2, 5, hair); R(hx + hw - 2, top + 4, 2, 5, hair); break;
    case 'long': cap(6, hair); break;
    case 'bob': cap(6, hair); R(hx - hw + 2, top + 5, hw * 2 - 4, 2, hair); break;
    case 'bun': cap(5, hair); R(hx - 3, top - 5, 6, 5, hair); R(hx - 2, top - 6, 4, 1, hair); break;
    case 'ponytail': cap(5, hair); break;
    case 'curly':
      for (let dy = -hh - 3; dy < -hh + 7; dy++) {
        const w = hw + 3 - Math.max(0, -hh + 2 - dy);
        R(hx - w, hcy + dy, w * 2, 1, hair);
        for (let x = -w + ((dy + 9) % 3); x < w; x += 3) R(hx + x, hcy + dy, 1, 1, '#00000055');
      }
      break;
    case 'mohawk': R(hx - 2, top - 5, 4, 9, hair); break;
    case 'beanie':
      cap(7, look.accent, 2); R(hx - hw - 1, top + 4, hw * 2 + 2, 2, '#00000044'); R(hx - 2, top - 5, 4, 3, look.accent); break;
    case 'cap':
      cap(5, look.accent, 1); R(hx - hw - 6, top + 4, hw + 6, 2, look.accent); R(hx - hw - 6, top + 5, hw + 6, 1, '#00000044'); break;
    case 'hat':
      R(hx - hw - 6, top + 3, hw * 2 + 12, 2, '#1f1f24'); R(hx - hw + 1, top - 5, hw * 2 - 2, 8, '#1f1f24'); R(hx - hw + 1, top + 1, hw * 2 - 2, 1, look.accent); break;
    case 'hood':
      R(hx - hw - 2, top - 2, hw * 2 + 4, 4, look.coat); R(hx - hw - 2, top, 3, hh + 6, look.coat); R(hx + hw - 1, top, 3, hh + 6, look.coat); break;
    default: R(hx - 3, top + 2, 3, 1, skin[0] === skin[1] ? skin[0] : '#ffffff33'); // bald: a little shine
  }
  if (alien && look.alienFace === 'third') { R(hx - 2, ey - 6, 4, 4, INK); R(hx - 1, ey - 5, 1, 1, PINK); }
  // Halloween: the costume goes on last, over everything, and comes off at the reveal (any reveal)
  if (look.costume && !reveal) drawCostume(R, look, { hx, hcy, hw, hh, fy, top, ey, eyes, my, t });
}

// The colour look's costumes. Ink and paper apart from the pumpkin, which is the event orange (#f08a2c).
const SHEET = '#ece8dc', SHEET_SHADE = '#c9c4b6', PUMPKIN = '#f08a2c', PUMPKIN_SHADE = '#b8601a';
function drawCostume(R, look, { hx, hcy, hw, hh, fy, top, ey, eyes, my, t }) {
  const blink = t % 140 < 4;
  const peek = () => { for (const ex of eyes) { if (blink) R(ex, ey + 1, 2, 1, INK); else { R(ex, ey, 2, 2, INK); R(ex + 1, ey, 1, 1, EYE_SHINE); } } };
  if (look.costume === 'mask') { // a pale full-face mask: dark eye holes, a nose hole and a mouth slit
    for (let y = ey - 4; y <= hcy + hh - 1; y++) {
      const w = Math.round((hw - 1) * Math.sqrt(Math.max(0, 1 - ((y - hcy - 1) / hh) ** 2)));
      R(hx - w, y, w * 2, 1, SHEET); R(hx + Math.round(w * 0.4), y, w - Math.round(w * 0.4), 1, SHEET_SHADE);
    }
    for (const ex of eyes) R(ex - 1, ey - 1, 4, 4, INK);
    peek();
    R(hx, ey + 3, 1, 2, INK); R(hx - 3, my + 1, 7, 1, INK);
    R(hx - hw - 1, ey, 2, 1, INK); R(hx + hw - 1, ey, 2, 1, INK); // the strap
    return;
  }
  if (look.costume === 'sheet') { // a sheet over the whole person, hem at the knees, two eye holes
    const crown = top - 7, hem = fy + 70;
    for (let y = crown; y <= hem; y++) {
      const dy = y - crown, head = hh + 6;
      const half = dy < head ? Math.round((hw + 6) * Math.sqrt(Math.max(0, 1 - ((head - dy) / head) ** 2)))
        : Math.round(hw + 6 + Math.min(16, (dy - head) * 0.6) + (dy - head) * 0.08);
      const rag = y > hem - 3 && ((hx + y) >> 2) & 1 ? 0 : 1; // a ragged hem
      if (!rag) continue;
      R(hx - half, y, half * 2, 1, SHEET); R(hx + Math.round(half * 0.45), y, half - Math.round(half * 0.45), 1, SHEET_SHADE);
      if (dy > head + 10 && dy % 2 === 0) R(hx - Math.round(half * 0.4), y, 1, 1, SHEET_SHADE); // a fold
    }
    for (const ex of eyes) R(ex - 1, ey - 1, 4, 4, INK);
    peek();
    return;
  }
  if (look.costume === 'pumpkin') { // a carved pumpkin over the head: lobes, ribs, a stem, a lit face
    const pr = hw + 6, ph = hh + 2, pcy = hcy - 1;
    for (let dy = -ph; dy <= ph; dy++) {
      const w = Math.round(pr * Math.sqrt(Math.max(0, 1 - (dy / ph) ** 2)));
      R(hx - w, pcy + dy, w * 2, 1, PUMPKIN); R(hx + Math.round(w * 0.5), pcy + dy, w - Math.round(w * 0.5), 1, PUMPKIN_SHADE);
      for (const k of [-0.5, 0.5]) R(hx + Math.round(k * w), pcy + dy, 1, 1, PUMPKIN_SHADE); // ribs
      R(hx - w, pcy + dy, 1, 1, INK); R(hx + w - 1, pcy + dy, 1, 1, INK);
    }
    R(hx - pr + 3, pcy - ph, pr * 2 - 6, 1, INK); R(hx - pr + 3, pcy + ph, pr * 2 - 6, 1, INK);
    R(hx - 1, pcy - ph - 4, 3, 4, '#3a2a1a'); R(hx + 2, pcy - ph - 5, 2, 1, '#3a2a1a'); // the stem
    for (const ex of eyes) for (let j = 0; j < 4; j++) R(ex + 1 - j, ey - 2 + j, 1 + j * 2, 1, INK); // triangle eyes
    peek();
    R(hx - 1, ey + 3, 2, 2, INK);
    R(hx - 6, my, 12, 2, INK); R(hx - 7, my - 1, 1, 1, INK); R(hx + 6, my - 1, 1, 1, INK);
    R(hx - 3, my, 2, 1, PUMPKIN); R(hx + 2, my + 1, 2, 1, PUMPKIN); // teeth
  }
}

// ======================= one bit: silhouettes behind reeded glass (studio/ART-BIBLE.md) =======================
// Shape, posture and eyes carry the character. A silhouette is derived from the colour look of the same knock, so
// both looks show the same person: same headwear (hat, hood or hair), same scarf, and height and build from the
// look's height and body width.

// the reeded glass panel, in glass pixels: size, where a visitor's head sits, the feet anchor below the glass
// that the approach scales about, and the snow horizon a refused visitor walks off towards
export const GLASS = { w: 180, h: 166, hx: 90, hy: 84, foot: 206, horizon: 66 };
// is glass pixel (u, v) inside the arch (the phone's punch-in keeps only the glass)
export const archAt = (u, v) => { const r = GLASS.w / 2; if (u < 0 || u >= GLASS.w || v >= GLASS.h || v < 0) return false; if (v >= r) return true; return Math.hypot(u - r + 0.5, v - r) < r; };

export function silhouetteOf(look) {
  const height = look.height >= 2 ? 'short' : look.height <= -2 ? 'tall' : 'average';
  const build = look.bodyW <= 21 ? 'slight' : look.bodyW >= 24 ? 'broad' : 'average';
  const side = look.sores[0][0] & 1 ? 1 : -1; // which way a brim, ponytail, scarf end or tilt points
  // posture by seed: most stand up straight; some stoop, tilt their head or drop a shoulder
  const posture = ['upright', 'upright', 'stoop', 'tilt', 'drop'][(((look.sores[1][1] % 5) + 5) % 5)];
  return {
    head: look.hairStyle, scarf: look.scarf, beard: look.beard, height, build, posture,
    rise: { short: -16, average: 0, tall: 16 }[height], // 32 glass px between a short and a tall crown
    rx: 22 + (look.headW - 9) * 2, ry: 27 + (look.headH - 11) * 2,
    shoulders: { slight: 54, average: 70, broad: 88 }[build], // broad is 63% wider than slight
    side,
    costume: look.costume ?? null, // Halloween: 'mask' | 'sheet' | 'pumpkin', drawn over the person, never a tell
    animal: look.animal ?? null, // Animal fever (game/animals.js): the head is an animal's, never a tell
    fail: look.fail ?? null, // who screams at a wrong Open (game/fails.js): null is the Knocker
    phase: (look.sores[2][0] + 7) * 18 + look.sores[2][1] + 8, // where in its idle loop a hand-drawn animal starts
  };
}
export const makeSilhouette = (seed, opts) => silhouetteOf(makeLook(seed, opts));

// Halloween costumes behind the glass (studio/sprints/3/item-1-design.md). Art only: a costume hides the visual
// guesses (headwear, beard, scarf, build) but never who is an alien; the reveal takes it off (glassState).
// Each returns [inside, shade] for one mask pixel; shade 1 = detail (a shade lighter), 2 = pale cloth, 3 = a carving.
const SHEET_W = 64; // one width for every sheet: it hides build
function costumeAt(kind, x, y, { hx, hy, rx, ry, MCX, hem }) {
  const dx = x - hx, ell = (cx, cy, ax, ay) => ((x - cx) / ax) ** 2 + ((y - cy) / ay) ** 2 <= 1;
  if (kind === 'sheet') { // a draped dome to a ragged hem at the knees; the eyes show through two holes
    const inSheet = (x, y) => {
      const hemY = hem + 34 + Math.abs(((x - MCX + 400) % 14) - 7) * 0.7;
      if (y < hy - ry - 4 || y >= hemY) return false;
      const k = Math.max(0, Math.min(1, (y - hy) / 46)), cx = hx + (MCX - hx) * k; // the head may tilt; the hem hangs straight
      const half = y < hy + 46 ? rx + 6 + (SHEET_W - rx - 6) * Math.sin(k * Math.PI / 2) : SHEET_W + (y - hy - 46) * 0.12;
      return y < hy ? ((x - hx) / (rx + 6)) ** 2 + ((y - hy) / (ry + 4)) ** 2 <= 1 : Math.abs(x - cx) < half;
    };
    if (!inSheet(x, y)) return [false, 0];
    const E = 6; // a dark hem of cloth round the edge, so the pale sheet holds its shape against the lit glass
    if (!inSheet(x - E, y) || !inSheet(x + E, y) || !inSheet(x, y - E) || !inSheet(x, y + E)) return [true, 0];
    const ex = Math.round(rx * 0.36), hole = [-1, 1].some(s => ell(hx + s * ex, hy + 1, 6, 5)); // eye holes
    const fold = y > hy + 50 && (Math.abs(((x - MCX + 400) % 22) - 11) < 1); // long folds down the cloth
    return [true, hole ? 0 : fold ? 1 : 2];
  }
  if (kind === 'pumpkin') { // a squat lobed pumpkin, wider than any head, a stem, carved eyes and grin
    const py = hy - 2, PR = 33, PY = 26;
    const lobes = ell(hx, py, PR * 0.55, PY) || ell(hx - PR * 0.45, py + 1, PR * 0.58, PY - 2) || ell(hx + PR * 0.45, py + 1, PR * 0.58, PY - 2);
    const stem = y >= py - PY - 9 && y < py - PY + 3 && Math.abs(dx - 2 - (py - PY - y) * 0.35) < 3.5;
    if (!lobes && !stem) return [false, 0];
    if (stem) return [true, 0];
    const ey = hy + 1, ex = Math.round(rx * 0.36);
    for (const sx of [-1, 1]) { // triangle eyes, point up, round where the eyes sit
      const cx = hx + sx * Math.max(ex, 11), t = (y - (ey - 11)) / 15;
      if (t >= 0 && t <= 1 && Math.abs(x - cx) <= 1 + t * 9) return [true, 3];
    }
    const my = hy + 13, mx = Math.abs(dx);
    if (mx <= 22 && y >= my - (mx > 16 ? 5 : 0) && y < my + 8 - mx * 0.15) { // a grin with two square teeth
      const tooth = (mx >= 5 && mx <= 10 && y < my + 3) || (mx < 3 && y >= my + 4);
      return [true, tooth ? 0 : 3];
    }
    const rib = [-0.5, 0.5].some(k => Math.abs(dx - k * PR * 0.62 * Math.sqrt(Math.max(0, 1 - ((y - py) / PY) ** 2))) < 1);
    return [true, rib ? 1 : 0];
  }
  if (kind === 'mask') { // a pale face mask, brow to chin: big dark eye holes, a nose hole and a slit for a mouth
    if (!(ell(hx, hy + ry * 0.2, rx * 0.86, ry * 0.78) && y >= hy - ry * 0.42)) return [false, 0];
    const ex = Math.round(rx * 0.36);
    const hole = [-1, 1].some(s => ell(hx + s * ex, hy + 1, 7, 6))
      || (y >= hy + ry * 0.2 && y < hy + ry * 0.36 && Math.abs(dx) < 1 + (y - hy - ry * 0.2) * 0.4)
      || (Math.abs(dx) < rx * 0.36 && Math.abs(y - (hy + ry * 0.58)) < 1.5);
    return [true, hole ? 0 : 2];
  }
  return [false, 0];
}

// Animal fever's heads behind the glass (game/animals.js). Art only, like the costumes: [inside, shade] for one mask pixel,
// shade 1 = detail, 2 = pale, 3 = lit. The coat and legs stay the person's, so an animal stands at the door in a coat.
const ANIMAL_EYES = {
  dog: [], afro: [[-9, 2], [9, 2]], cat: [[-10, -1], [10, -1]], hatcat: [[-10, -1], [10, -1]],
  frog: [[-16, -10], [16, -10]], pigeon: [[-7, 0], [7, 0]], hamster: [[-11, 2], [11, 2]], capy: [[-13, -3], [13, -3]], goat: [[-8, 0], [8, 0]],
};
// the hand-drawn heads (client/animal-art.js) paint their own eyes; without their sheet the nearest code-drawn animal stands in,
// and a degen visitor (game/degens.js) is a person again
const DRAWN_FALLBACK = { cowboy: 'dog', afrocat: 'cat', owl: 'hamster', raccoon: 'cat' };
const NO_NECK = new Set(['balloon']); // the hopium balloon floats on its string over an empty collar
const codeDrawn = kind => { const k = DRAWN_FALLBACK[kind] ?? kind; return k in ANIMAL_EYES ? k : null; };
function animalAt(kind, x, y, { hx, hy, side, neckBase }) {
  const dx = x - hx, ax = Math.abs(dx), ell = (cx, cy, rx, ry) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1;
  const tri = (x1, y1, x2, y2, x3, y3) => { // inside the triangle
    const d1 = (x - x2) * (y1 - y2) - (x1 - x2) * (y - y2), d2 = (x - x3) * (y2 - y3) - (x2 - x3) * (y - y3), d3 = (x - x1) * (y3 - y1) - (x3 - x1) * (y - y1);
    return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
  };
  const ears = (bx, by, tx, ty) => tri(hx - bx, hy + by, hx - bx + 16, hy + by - 8, hx - tx, hy + ty) || tri(hx + bx, hy + by, hx + bx - 16, hy + by - 8, hx + tx, hy + ty);
  const whiskers = () => [10, 15].some(wy => ax >= 16 && ax < 40 && Math.abs(y - (hy + wy + (ax - 16) * (wy === 10 ? -0.15 : 0.15))) < 0.9);
  switch (kind) {
    case 'dog': { // pointy ears, a snout, sunglasses
      if (ell(hx, hy + 9, 5, 3.5)) return [true, 0];
      if (Math.abs(ax - 10) <= 8 && Math.abs(y - (hy - 1)) <= 5) return [true, 2];
      if (ax < 3 && y >= hy - 3 && y <= hy - 1) return [true, 2];
      if (ell(hx, hy + 15, 12, 9)) return [true, 1];
      return [ell(hx, hy, 24, 22) || ears(22, -8, 17, -46), 0];
    }
    case 'afro': { // a round afro, floppy ears, a snout
      const a = Math.atan2(y - (hy - 16), dx), r = Math.hypot(dx / 40, (y - (hy - 16)) / 30) * (1 - Math.sin(a * 9) * 0.06);
      if (ell(hx, hy + 9, 4, 3)) return [true, 0];
      if (ell(hx, hy + 16, 11, 8)) return [true, 1];
      if (r <= 1 && y < hy - 2) return [true, (x * 7 + y * 3) % 13 === 0 ? 1 : 0];
      return [ell(hx, hy + 4, 22, 20) || ell(hx - 22, hy + 8, 7, 14) || ell(hx + 22, hy + 8, 7, 14), 0];
    }
    case 'cat': { // ears, whiskers, a bow tie at the collar
      const by = neckBase + 4;
      if (ax <= 14 && ax >= 2 && Math.abs(y - by) <= ax * 0.45 + 1 || (ax < 3 && Math.abs(y - by) < 3)) return [true, 2];
      if (whiskers()) return [true, ell(hx, hy + 2, 24, 20) ? 2 : 0];
      return [ell(hx, hy + 2, 24, 20) || ears(23, -6, 20, -38), 0];
    }
    case 'hatcat': { // the cat with a bowler hat, its ears poking out under the brim
      if (y >= hy - 21 && y < hy - 18 && ax < 20) return [true, 2]; // the hat band
      if (y >= hy - 18 && y < hy - 13 && ax < 34) return [true, 0]; // the brim
      if (ell(hx, hy - 26, 20, 14) && y < hy - 13) return [true, 0]; // the crown
      if (whiskers()) return [true, ell(hx, hy + 2, 24, 20) ? 2 : 0];
      return [ell(hx, hy + 2, 24, 20) || tri(hx - 24, hy - 4, hx - 12, hy - 14, hx - 32, hy - 24) || tri(hx + 24, hy - 4, hx + 12, hy - 14, hx + 32, hy - 24), 0];
    }
    case 'frog': { // a wide head, eye bumps, a grin, a little crown between the bumps
      if (ax < 24 && Math.abs(y - (hy + 14 + dx * dx / 180)) < 1.2) return [true, 2];
      if ([-8, 0, 8].some(k => ell(hx + k, hy - 26, 1.6, 1.6))) return [true, 3]; // the crown's jewels
      const crown = (y >= hy - 30 && y < hy - 20 && ax < 13) || [-9, 0, 9].some(k => tri(hx + k - 4, hy - 30, hx + k + 4, hy - 30, hx + k, hy - 40));
      return [crown || ell(hx, hy + 8, 32, 19) || ell(hx - 16, hy - 10, 11, 10) || ell(hx + 16, hy - 10, 11, 10), 0];
    }
    case 'pigeon': { // a round head with a tuft, a big beak to one side, a gold chain
      if (ax < 26 && Math.abs(y - (neckBase + 6 + dx * dx / 60)) < 1.5 && (x + y) % 4 < 2) return [true, 3];
      const tuft = [-6, 0, 6].some(k => tri(hx + k - 3, hy - 17, hx + k + 3, hy - 17, hx + k + side * 2, hy - 29));
      return [tuft || ell(hx, hy + 2, 22, 21) || tri(hx + side * 17, hy - 4, hx + side * 17, hy + 10, hx + side * 38, hy + 4), 0];
    }
    case 'hamster': { // round and wide, round ears, two teeth, headphones
      if (ell(hx - 31, hy + 2, 7, 12) || ell(hx + 31, hy + 2, 7, 12)) return [true, 1];
      const band = ((dx / 36) ** 2 + ((y - hy + 2) / 34) ** 2), inBand = band <= 1 && band >= 0.84 && y < hy - 4;
      if (inBand) return [true, 0];
      if (ax < 3 && y >= hy + 16 && y < hy + 21) return [true, 2];
      if (ell(hx, hy + 10, 3, 2) || ell(hx - 20, hy - 20, 4, 4) || ell(hx + 20, hy - 20, 4, 4)) return [true, 1];
      return [ell(hx, hy + 4, 30, 26) || ell(hx - 20, hy - 20, 8, 8) || ell(hx + 20, hy - 20, 8, 8), 0];
    }
    case 'capy': { // a boxy head, small ears, an orange balanced on top
      if (ell(hx, hy - 27, 11, 10)) return [true, 3];
      if (tri(hx + 2, hy - 37, hx + 10, hy - 42, hx + 5, hy - 34)) return [true, 0];
      if (ell(hx - 7, hy + 15, 2.5, 2) || ell(hx + 7, hy + 15, 2.5, 2)) return [true, 2];
      const cx = Math.max(0, ax - 19), cy = Math.max(0, hy - 8 - y, y - (hy + 16));
      return [(ax < 27 && y >= hy - 16 && y <= hy + 24 && Math.hypot(cx, cy) <= 8) || ell(hx - 21, hy - 17, 6, 5) || ell(hx + 21, hy - 17, 6, 5), 0];
    }
    case 'goat': { // a long face, a beard, curled horns, a Roman helmet with a tall brush crest
      if (ax < 5 && y >= hy - 58 && y < hy - 26) return [true, x % 2 === 0 ? 1 : 0]; // the crest, brushed upright
      if (y >= hy - 13 && y < hy - 10 && ax < 24) return [true, 2]; // the helmet's rim
      if (ax >= 18 && ax < 23 && y >= hy - 10 && y < hy + 10) return [true, y % 6 === 0 ? 2 : 0]; // cheek guards, riveted
      const horn = [-1, 1].some(sg => { const r = Math.hypot(x - (hx + sg * 31), y - (hy - 14)); return r >= 6 && r < 11 && (y > hy - 18 || (x - hx) * sg > 31); });
      return [horn || (ell(hx, hy - 12, 24, 19) && y < hy - 10) || ell(hx, hy + 4, 17, 26) || ell(hx - 26, hy + 2, 9, 4) || ell(hx + 26, hy + 2, 9, 4)
        || tri(hx - 6, hy + 26, hx + 6, hy + 26, hx, hy + 42), 0];
    }
    default: return [false, 0];
  }
}

// The silhouette as a mask (1 = the person), in its own pixels: w x h, head centre at (cx, oy) when standing still.
// d marks interior detail (a hat band, a beanie cuff, scarf stripes) that the glass shows a shade lighter.
// opts: stretch 0..1 (an alien's neck and head rise), hands (pressed up on the glass), back (turned away, no eyes),
// stride 0 | 1 | 2 (standing, left or right foot forward), hunch (the head drops, in pixels / 5).
const MW = 180, MH = 400, MCX = 90, MOY = 120, FEET = MOY + 250;
const maskCache = new Map();
export function silhouetteMask(sil, { stretch = 0, hands = false, back = false, stride = 0, hunch = 0, t = 0 } = {}) {
  // a hand-drawn animal's idle loop: the frame is part of the key, the clock is not
  const drawn = !stretch && sil.animal && drawnAnimal(sil.animal) ? sil.animal : null, frame = drawn ? headFrame(drawn, t, sil.phase ?? 0) : -1;
  const key = JSON.stringify([sil, stretch, hands, back, stride, hunch, frame]);
  if (maskCache.has(key)) return maskCache.get(key);
  const m = new Uint8Array(MW * MH), d = new Uint8Array(MW * MH);
  const s = stretch, side = sil.side, posture = sil.posture ?? 'upright';
  const rx = sil.rx * (1 + 0.1 * s), ry = sil.ry * (1 + 0.3 * s); // the head swells long, and wide enough for its eyes
  const tilt = posture === 'tilt' ? side * 7 : 0, stoop = posture === 'stoop' ? 9 : 0;
  const hx = MCX + Math.round(tilt * (1 - s)), hy = MOY - Math.round(44 * s) + hunch * 5 + stoop;
  const neckBase = MOY + sil.ry + 4; // shoulders do not move when the head rises
  const sh = sil.shoulders;
  const drop = posture === 'drop' ? 8 : 0; // one shoulder sits lower (on the far side from `side`)
  const hunchUp = stoop ? 6 : 0; // a stoop rounds the shoulders up around the neck
  const ell = (x, y, cx, cy, ax, ay) => ((x - cx) / ax) ** 2 + ((y - cy) / ay) ** 2 <= 1;
  const top = hy - ry; // top of the skull
  function hair(x, y) { // headwear and hair: [inside, detail]
    const dx = x - hx;
    switch (sil.head) {
      case 'short': return [ell(x, y, hx, hy - 3, rx + 2, ry + 1) && y < hy, 0];
      case 'long': return [ell(x, y, hx, hy - 2, rx + 4, ry + 3) || (y >= hy && y < hy + ry + 18 && Math.abs(dx) < rx + 4 - (y - hy) * 0.08), 0];
      case 'bob': return [ell(x, y, hx, hy - 2, rx + 6, ry + 3) && y < hy + ry * 0.55, 0];
      case 'bun': return [ell(x, y, hx, top - 5, 9, 8) || (ell(x, y, hx, hy - 3, rx + 2, ry + 1) && y < hy), 0];
      case 'ponytail': {
        const px0 = hx + side * (rx + 7);
        return [(ell(x, y, hx, hy - 3, rx + 2, ry + 1) && y < hy) || ell(x, y, hx + side * (rx + 4), hy - ry * 0.45, 7, 6)
          || (Math.abs(x - px0) < 3 + (y - hy) * 0.04 && y >= hy - ry * 0.45 && y < hy + ry * 0.9), 0];
      }
      case 'curly': {
        const a = Math.atan2(y - (hy - 3), dx), r = 1 + 0.09 * Math.sin(a * 9);
        return [y < hy + ry * 0.35 && ((dx / ((rx + 6) * r)) ** 2 + ((y - hy + 3) / ((ry + 5) * r)) ** 2 <= 1), 0];
      }
      case 'mohawk': return [Math.abs(dx) < 4 && y >= top - 17 + ((x >> 1) & 1) * 3 && y < top + 6, 0];
      case 'beanie': { // a tall slouchy dome over a wide, ribbed cuff, and a small bobble
        const cuffTop = hy - ry * 0.62, cuffBot = hy - ry * 0.3;
        const dome = ell(x, y, hx + side * 3, cuffTop, rx + 1, ry * 0.95) && y < cuffTop;
        const cuff = y >= cuffTop && y < cuffBot && Math.abs(dx) < rx + 5;
        const bobble = ell(x, y, hx + side * 6, cuffTop - ry * 0.95 - 3, 6, 5);
        return [dome || cuff || bobble, cuff && (Math.round(y - cuffTop) <= 1 || (x & 3) === 0) ? 1 : 0];
      }
      case 'cap': { // a round crown and a long, thick peak to one side
        const dome = ell(x, y, hx, hy - 3, rx + 1, ry + 1) && y < hy - ry * 0.4;
        const lo = side > 0 ? hx - rx : hx - rx - 28, hi = side > 0 ? hx + rx + 28 : hx + rx;
        const brim = y >= hy - ry * 0.62 && y < hy - ry * 0.34 && x >= lo && x <= hi;
        return [dome || brim, brim && y === Math.round(hy - ry * 0.62) ? 1 : 0];
      }
      case 'hat': { // a tall crown on a wide, thick brim
        const by = hy - ry * 0.55;
        const brim = y >= by - 3 && y < by + 5 && Math.abs(dx) < rx + 20 - (y > by + 1 ? 3 : 0);
        const crown = Math.abs(dx) < rx - 1 && y >= top - 18 && y < by && !(Math.abs(dx) > rx - 4 && y < top - 15);
        return [brim || crown, crown && y >= by - 8 && y < by - 3 ? 1 : 0];
      }
      case 'hood': {
        const cowl = ell(x, y, hx, hy - 3, rx + 10, ry + 9);
        const peak = y >= top - 24 && y < top && Math.abs(dx - side * 4) < (y - (top - 24)) * 0.75;
        const drape = y >= hy && y < neckBase + 12 && Math.abs(dx) < rx + 8 + (y - hy) * 0.3;
        return [cowl || peak || drape, 0];
      }
      default: return [false, 0]; // bald
    }
  }
  // a costume is worn only standing at the glass (the reveal, the hands and the walk away are without it)
  const costume = !stretch && !hands && !back ? sil.costume ?? null : null;
  const animal = !stretch && sil.animal && !drawn ? codeDrawn(sil.animal) : null; // an alien stretching at the reveal is no animal
  const covers = costume === 'sheet' || costume === 'pumpkin' || !!animal || !!drawn; // all hide hair and headwear
  const legW = Math.round(sh * 0.3), hem = MOY + 175, gap = back ? 9 : 5;
  // from behind the arms hang apart from the coat, with a clear gap, and end at the hands well above the hem
  const armW = Math.round(sh * 0.22), bodyHalf = sh - armW - 7, wrist = hem - 34;
  for (let y = 0; y < MH; y++) for (let x = 0; x < MW; x++) {
    const dx = x - hx, i = y * MW + x, cdx = x - MCX;
    let on = !animal && !drawn && ell(x, y, hx, hy, rx, ry);
    if (sil.beard && !back && !costume && !animal && !drawn) on ||= ell(x, y, hx, hy + ry * 0.5, rx * 0.82, ry * 0.62);
    if (!NO_NECK.has(drawn)) on ||= Math.abs(dx) < Math.max(7, rx * 0.45) && y >= hy && y <= neckBase + 4; // neck (long when stretched)
    const dy = y - neckBase + hunchUp - (cdx * side < 0 ? drop : 0);
    if (dy >= 0 && y < hem) { // shoulders round off, then the coat falls to the hem
      const half = dy < 20 ? sh * Math.sqrt(1 - ((20 - dy) / 20) ** 2) : sh - (dy - 20) * (0.04 + sh * 0.0015);
      if (!back || dy < 34) { if (Math.abs(cdx) < Math.max(rx * 0.45, half)) on = true; }
      else {
        if (Math.abs(cdx) < Math.max(rx * 0.45, bodyHalf - (dy - 34) * 0.05)) on = true; // the back of the coat
        for (const sgn of [-1, 1]) { // arms, swinging a little with the stride
          const swing = stride && (stride === 1) === (sgn > 0) ? 5 : 0;
          const ax = MCX + sgn * (sh - armW / 2 - 1 + ((dy - 34) / 120) * (swing + 2));
          if (Math.abs(x - ax) < armW / 2 && y < wrist - swing) on = true;
          if (ell(x, y, ax, wrist - 2 - swing, armW / 2 + 1, 7)) on = true; // a hand
        }
      }
    }
    if (y >= hem && y < FEET) { // legs; walking, the back leg lifts its heel and shows the sole
      for (const [k, lx] of [[1, MCX - gap - legW / 2], [2, MCX + gap + legW / 2]]) {
        const up = stride === k, lift = up ? 16 : 0, lean = (k === 1 ? -1 : 1) * (up ? 5 : stride ? 2 : 0) * (y - hem) / (FEET - hem);
        const w = up ? legW / 2 - 1 : legW / 2;
        if (Math.abs(x - lx - lean) < w && y < FEET - lift) on = true;
        if (up) { if (y >= FEET - lift - 9 && y < FEET - lift + 2 && Math.abs(x - lx - lean) < w + 1) on = true; } // the sole
        else if (y >= FEET - 6 && y < FEET && Math.abs(x - lx - lean) < legW / 2 + 2) on = true; // a planted shoe
      }
    }
    if (costume === 'sheet') on = y >= hem && y < FEET && on; // under the sheet only the legs below the hem show
    const [h, det] = covers ? [false, 0] : hair(x, y);
    if (h) { on = true; if (det) d[i] = 1; }
    if (sil.scarf && costume !== 'sheet') {
      const wrap = ell(x, y, hx, neckBase + 2, rx + 6, 9);
      const tail = y >= neckBase && y < neckBase + 48 && (side > 0 ? dx >= 6 && dx < 17 : dx <= -6 && dx > -17);
      if (wrap || tail) { on = true; if (tail && (y - neckBase) % 8 < 2) d[i] = 1; if (wrap && y === neckBase + 2) d[i] = 1; }
    }
    if (hands) { // raised arms, long fingers spread on the glass
      for (const sgn of [-1, 1]) {
        const px0 = hx + sgn * (sh - 6), py0 = MOY - 8;
        const ax = hx + sgn * (sh - 16), ay = neckBase + 18; // elbow from the shoulder up to the palm
        const t = Math.max(0, Math.min(1, ((x - ax) * (px0 - ax) + (y - ay) * (py0 - ay)) / ((px0 - ax) ** 2 + (py0 - ay) ** 2)));
        if (Math.hypot(x - (ax + (px0 - ax) * t), y - (ay + (py0 - ay) * t)) < 8) on = true;
        if (ell(x, y, px0, py0, 12, 10)) on = true;
        for (let f = 0; f < 4; f++) { // fingers too long for a person, splayed
          const fx = px0 + (f - 1.5) * 7 * sgn, len = f === 1 || f === 2 ? 34 : 26;
          if (Math.abs(x - fx - (py0 - y) * (f - 1.5) * 0.18) < 2.6 && y <= py0 && y > py0 - len) on = true;
        }
      }
    }
    if (costume) {
      const [c, shade] = costumeAt(costume, x, y, { hx, hy, rx, ry, MCX, hem });
      if (c) { on = true; d[i] = shade; }
      else if (costume === 'pumpkin' && on && y < hy + ry * 0.55 && !(Math.abs(dx) < Math.max(7, rx * 0.45) && y >= hy)) on = false; // no head shape outside the pumpkin
    }
    if (animal) {
      const [a, shade] = animalAt(animal, x, y, { hx, hy, side, neckBase });
      if (a) { on = true; d[i] = back ? 0 : shade; }
    }
    if (on) m[i] = 1;
  }
  if (drawn) { // the hand-drawn head, its centre on the head's, mirrored when the figure faces the other way
    const art = headArt(drawn, frame), [ax, ay] = art.anchor, flip = side < 0;
    for (let k = 0; k < art.px.length; k += 3) {
      const x = hx + (flip ? ax - art.px[k] : art.px[k] - ax), y = hy + art.px[k + 1] - ay;
      if (x < 0 || y < 0 || x >= MW || y >= MH) continue;
      m[y * MW + x] = 1; d[y * MW + x] = back ? 0 : art.px[k + 2];
    }
  }
  let topRow = MH;
  for (let i = 0; i < m.length; i++) if (m[i]) { topRow = Math.floor(i / MW); break; }
  const ex = Math.round(rx * (0.36 + 0.06 * s)); // an alien's first pair sits wider as it stretches
  const M = {
    w: MW, h: MH, m, d, cx: MCX, oy: MOY, top: topRow, feet: FEET,
    head: [hx, hy, rx, ry], mouth: [hx, Math.round(hy + ry * 0.5)],
    eyes: back || drawn ? [] : animal ? ANIMAL_EYES[animal].map(([ox, oy]) => [hx + ox, hy + oy]) : [[hx - ex, hy + 1], [hx + ex, hy + 1]],
  };
  if (maskCache.size > 64) maskCache.clear();
  maskCache.set(key, M);
  return M;
}

// Where the figure lands in the glass: GLASS.w x GLASS.h, 1 where drawGlass paints the person (the same reed
// wobble and mapping, no glitch tears). Used to measure height, crown and shoulders in glass (logical) pixels.
export function glassFigure(gs) {
  const { w: GW, h: GH } = GLASS, out = new Uint8Array(GW * GH);
  const M = gs.M && !gs.hidden ? gs.M : null;
  if (!M) return out;
  const r = GW / 2;
  for (let v = 0; v < GH; v++) for (let u = 0; u < GW; u++) {
    if (v < r && Math.hypot(u - r + 0.5, v - r) >= r) continue; // outside the arch
    const su = u + Math.sin(u / 7 * Math.PI * 2) * 1.4;
    const x = Math.floor(M.cx + (su - gs.ax) / gs.sx), y = Math.floor(M.oy + (v - gs.hv) / gs.sy);
    if (x >= 0 && y >= 0 && x < M.w && y < M.h && M.m[y * M.w + x]) out[v * GW + u] = 1;
  }
  return out;
}

// Paint the reeded glass and whoever is behind it into a one-bit raster B (bits.js), top-left at (gx, gy).
// gs comes from glassState() in reactions.js: where the figure stands, how far away, its eyes, and the reveal.
const hash2 = (a, b) => { let h = (a * 374761393 + b * 668265263) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

// mask shade: none, detail, pale cloth, a carving with the lamp behind it, white-hot (a lit knock's lit wire, neighbours-lit.js)
const SHADE = [0, 0.2, 0.42, 0.7, 1];
// a bull or bear knock's beam (beasts.js), in glass pixels: half its width at row v. It opens out under the lamp,
// then falls nearly straight to the sill at 142 of 180 px (79%), so an ink wedge stays on each side from just under
// the event plate down to the sill. flare (the reveal) opens it wider, so the bull's swung horns stay in the light.
export const BEAM = { sill: 71, open: 19, core: { v: 29, rx: 15, ry: 11 } };
export const beamHalf = (v, flare = 0) => BEAM.sill - BEAM.open * (1 - Math.min(v, GLASS.h) / GLASS.h) ** 2 + flare;
export function drawGlass(B, gx, gy, gs) {
  const { w: GW, h: GH, horizon: HZ } = GLASS;
  const { px, get, S } = B;
  const lamp = gs.lamp ?? 1, fr = gs.fr ?? 0, t = gs.t ?? 0;
  const M = gs.M && !gs.hidden ? gs.M : null;
  const inArch = (u, v) => { const r = GW / 2; if (u < 0 || u >= GW || v >= GH || v < 0) return false; if (v >= r) return true; return Math.hypot(u - r + 0.5, v - r) < r; };
  const inside = (mx, my) => { const x = Math.floor(mx), y = Math.floor(my); return x >= 0 && y >= 0 && x < M.w && y < M.h && M.m[y * M.w + x] === 1; };
  const glowing = (gs.glow ?? 0) > 0;
  const roof = u => 2 + Math.floor(hash2(u >> 3, 7) * 6) + ((u & 7) === 3 && hash2(u >> 3, 8) < 0.4 ? 3 : 0); // a chimney now and then
  let K = 0, HL = 0, HOT = 0, DK = 0;
  // a bull or bear knock (beasts.js): a cone of its own light from the lamp above the arch; the glass outside it dims
  const beam = gs.beam ?? null;
  // the lamp's white-hot mouth: a paper half-disc at the top centre of the cone, just under the event plate
  const C = BEAM.core;
  const coreD = (u, v) => (v < C.v - 2 ? 9 : Math.hypot((u - GW / 2 + 0.5) / C.rx, (v - C.v) / C.ry));
  const inBeam = beam ? (u, v) => Math.abs(u - GW / 2 + 0.5) < beamHalf(v, beam.flare) : null;
  const lum = (u, v) => {
    const lp = Math.max(0, 1 - Math.hypot(u - 104, (v - 40) * 1.1) / 130);
    let L = (0.42 + lp * 0.62) * lamp;
    if (beam) {
      const d = coreD(u, v);
      L = d < 1 ? 1.2 * Math.min(1, lamp) // the core
        : inBeam(u, v) ? Math.max(L, (0.6 + 0.38 * (1 - v / GH) + Math.max(0, 0.5 - (d - 1) * 0.5)) * Math.min(1.1, lamp))
          : L * 0.12; // outside the cone the glass is close to ink, so the cone's edge is a line, not a fade
    }
    if (gs.ground) L = v >= HZ ? 0.92 : L * 0.55; // the street outside: night above, snow below
    if (glowing) L *= 1 - 0.85 * gs.glow; // the porch light sickens and dims, so the glowing figure is what you see
    K = 0; HL = 0; HOT = 0; DK = 0;
    if (!M) return L;
    const mx = M.cx + (u - gs.ax) / gs.sx, my = M.oy + (v - gs.hv) / gs.sy;
    if (!inside(mx, my)) {
      if (glowing) { const h = 3.5 / gs.sx; if (inside(mx + h, my) || inside(mx - h, my) || inside(mx, my + h) || inside(mx, my - h)) { HL = 1; return Math.max(L, 0.95); } }
      return L;
    }
    K = 1;
    const dd = Math.min(2.5 / gs.sx, 4), lit = Math.min(1, lamp); // a far figure keeps a thin rim, not a lit head
    const edge = !inside(mx + dd, my) ? 0.9 * lit : !inside(mx - dd, my) ? 0.35 * lit : !inside(mx, my - dd) ? 0.5 * lit : 0;
    const dk = M.d[Math.floor(my) * M.w + Math.floor(mx)], detail = SHADE[dk];
    DK = dk;
    if (dk === 4) HOT = 1; // a white-hot wire: paper, never the beam's tint
    if (gs.sores && hash2(Math.floor(mx / 5), Math.floor(my / 5)) < 0.1 && hash2(Math.floor(mx / 2), Math.floor(my / 2) + 99) < 0.75) K = 2;
    return L + (0.02 + edge + detail - L) * gs.pres; // far away the visitor is only a grey smear
  };
  const glassTint = gs.outbreak ? TN.green : 0;
  // an outbreak's plain eyes keep its green; everything else in the eye comes from the sheet's own palette
  const paperTint = gs.eyeTint === TN.green ? TN.green : 0;
  const eyePix = new Uint8Array(GW * GH);
  if (gs.eyesOn && gs.eye) for (const { s, x, y } of eyeStamps(gs.eye, gs.eyes)) {
    for (let j = 0; j < s.h; j++) for (let i = 0; i < s.w; i++) {
      const u = x + i, v = y + j;
      if (s.idx[j * s.w + i] && u >= 0 && u < GW && v >= 0 && v < GH) eyePix[v * GW + u] = s.idx[j * s.w + i];
    }
  }
  // a level 2 night door (night.js): plain glass shows the Aseprite door's own glass; a bull, bear or lit knock's beam, an outbreak and an
  // infected glow keep the one-bit glass (as the approved A5 L2 files do). Only the `art` codes change: bits and tints are as before.
  const plain = !beam && !glassTint && !glowing, keepBit = plain ? 0 : ART.ONEBIT;
  for (let v = 0; v < GH; v++) {
    let shift = 0;
    if (gs.glitch && hash2(v >> 2, fr) < 0.22) shift = (hash2(v, fr * 3) - 0.5) * 22;
    for (let u = 0; u < GW; u++) {
      if (!inArch(u, v)) continue;
      const flute = u % 7, uu = u + shift;
      const su = uu + Math.sin(uu / 7 * Math.PI * 2) * 1.4;
      let L = 0, halo = false;
      if (gs.crisp) { // a small figure far off in the snow: one sharp ink shape, haloed by the glare off the snow
        L = lum(su, v);
        if (gs.ground && !K && (v === HZ || (v < HZ && v >= HZ - roof(u)))) L = 0; // the ground line, far rooftops
        if (M && !K) { const mx = M.cx + (su - gs.ax) / gs.sx, my = M.oy + (v - gs.hv) / gs.sy, h = 2 / gs.sx;
          // a solid paper rim, so the head stays clear against the rooftops and the night sky
          if (inside(mx + h, my) || inside(mx - h, my) || inside(mx, my - h) || inside(mx, my + h)) { L = 1; halo = true; } }
        if (K) L = 0;
      } else {
        for (let k = -1; k <= 1; k++) L += lum(su, v + k * 2); L /= 3;
        lum(su, v);
        if (gs.ground && !K && v === HZ) L = 0;
      }
      if (glowing && K) L = Math.max(L, 0.15 + 0.45 * gs.glow);
      if (K === 2) L = 0;
      S.tint = glassTint || (glowing && (K || HL) ? TN.green : 0);
      if (beam && inBeam(su, v)) S.tint = coreD(su, v) < 1 && !K ? 0 : beam.tint; // the beam's colour wins over an outbreak's
      if (HOT) S.tint = 0;
      if (flute === 0) L *= 0.6;
      if (flute === 3) L = Math.min(1, L * 1.25 + 0.03);
      let c = halo ? 1 : dither(gx + u, gy + v, L);
      const e = eyePix[v * GW + u]; // the eye sprites, cut from the Aseprite sheet (eyes.js): 0 clear, 1 ink, 2 paper, 3 red, 4 green
      if (e) { c = e === 1 ? 0 : 1; S.tint = e === 3 ? TN.red : e === 4 ? TN.green : e === 2 ? paperTint : 0; }
      // the visitor on a night door: its lit pixels (the lamp's rim, the reeds' glints) are the glass's cool lit blue; pale cloth stays cream
      if (K && !e && plain && DK < 2 && !gs.crisp) S.art = ART.FIG;
      else if (e || K || halo || HL) S.art = e ? 0 : keepBit;
      else if (!plain) S.art = ART.ONEBIT;
      else { const ru = Math.round(uu); S.art = inArch(ru, v) ? glassArt(ru, v) : glassArt(u, v); }
      px(gx + u, gy + v, c);
      S.tint = 0; S.art = 0;
    }
  }
  // footprints in the snow, then the snow itself
  for (const [u, v, pw = 1, ph = 1] of gs.prints ?? []) { // ink prints, bigger the nearer they are
    for (let j = 0; j < ph; j++) for (let i = 0; i < pw; i++) {
      if ((i === 0 || i === pw - 1) && (j === 0 || j === ph - 1) && pw > 2 && ph > 1) continue; // rounded ends
      const uu = u - (pw >> 1) + i, vv = v - (ph >> 1) + j;
      if (inArch(uu, vv) && vv > HZ) px(gx + uu, gy + vv, 0);
    }
  }
  // cough clouds: dithered plague-green puffs (a bull's snort: paper, gs.puffTint)
  for (const { u: cu, v: cv, r, dens } of gs.puffs ?? []) {
    for (let j = -r; j <= r; j++) for (let i = -r; i <= r; i++) {
      const dist = Math.hypot(i, j) / r, u = Math.round(cu + i), v = Math.round(cv + j);
      if (dist > 1 || !inArch(u, v)) continue;
      S.tint = gs.puffTint ?? TN.green; // an ink rim, so a cloud reads on the glowing figure as well as on the dark glass
      S.art = keepBit;
      if (dist > 0.8) px(gx + u, gy + v, 0);
      else px(gx + u, gy + v, dither(gx + u, gy + v, 0.35 + (1 - dist) * 0.6 * dens));
      S.tint = 0; S.art = 0;
    }
  }
  const flakes = Math.round(46 * (gs.snow ?? 1)), N = LEAF.w * LEAF.h;
  // a night door's snow on its glass is L2's snow blue (A1); a flake over the visitor stays the one-bit flake
  const flakeArt = (x, y) => { const a = B.art?.[y * B.w + x + S.ox]; return plain && a > 0 && a <= N ? ART.SNOW : a ?? 0; };
  for (let i = 0; i < flakes; i++) {
    const sp = 8 + hash2(i, 1) * 10, ph = hash2(i, 2) * GH;
    const v = Math.floor((ph + t * sp) % GH), u = Math.floor((hash2(i, 3) * GW + Math.sin(t * 0.8 + i) * 4 + GW) % GW);
    S.tint = glassTint;
    if (inArch(u, v) && u % 7 !== 0) {
      const c = get(gx + u + S.ox, gy + v) ? 0 : 1;
      S.art = flakeArt(gx + u, gy + v); px(gx + u, gy + v, c);
      if (hash2(i, 4) < 0.3 && inArch(u + 1, v)) { S.art = flakeArt(gx + u + 1, gy + v); px(gx + u + 1, gy + v, c); }
    }
    S.tint = 0; S.art = 0;
  }
  // the dotted lead around the arch (a night door: the lead of the Aseprite door, A1's)
  for (let v = -3; v < GH + 3; v++) for (let u = -3; u < GW + 3; u++) {
    if (!inArch(u, v) && (inArch(u + 3, v) || inArch(u - 3, v) || inArch(u, v + 3) || inArch(u, v - 3))) { S.art = plain ? glassArt(u, v) : keepBit; px(gx + u, gy + v, (u + v) & 1 ? 1 : 0); }
  }
  S.art = 0;
  // the Knocker (knocker.js, reactions.js glassState): an alien's revealed shape, cut from the Aseprite sheets, in front of
  // the snow as in A4; its fingers hook over the glass's foot (the scene paints them again over a rule bar or the sum: knockerHands)
  const kn = gs.knocker && knockerSheet();
  if (kn) {
    paintKnocker(B, gx, gy, kn.body(gs.knocker.f), gs.knocker.base, inArch, kn.dark?.(gs.knocker.f), ART.DARK);
    knockerHands(B, gx, gy, gs, gs.knocker.base);
  }
  // red moment 2, the wrong Open (red.js, from the Aseprite effect sheets): the flood over a person's glass (not over an eye), the Knocker's
  // scream with the flood laid over its dome, and the cracks that stay
  if (gs.flood) paintFlood(B, gx, gy, gs.flood.f, (u, v) => eyePix[v * GW + u] !== 0);
  if (gs.scream) paintScream(B, gx, gy, gs.scream.f, inArch, { flood: gs.scream.flood, skin: gs.scream.skin });
  if (gs.cracks) paintCracks(B, gx, gy, gs.cracks.f);
  if (gs.stamp) paintStamp(B, gx, gy); // the wrong Open's OPENED. stamp, low in the glass where A3 has it
  // red moment 1, the last 10 s: the creep, on paper pixels only
  if (gs.creep) paintCreep(B, gx, gy, gs.creep.f);
}
// the Knocker's three fingers a side, their hooks on glass row `base` (inside the arch, and the few rows of the frame below it)
export function knockerHands(B, gx, gy, gs, base = gs.knocker?.handsBase) {
  const kn = gs.knocker && knockerSheet();
  if (!kn) return 0;
  const { w: GW, h: GH } = GLASS, r = GW / 2;
  const keep = (u, v) => u >= 0 && u < GW && v >= 0 && (v >= r ? v < GH + 4 : Math.hypot(u - r + 0.5, v - r) < r);
  return paintKnocker(B, gx, gy, kn.hands(gs.knocker.f), base, keep);
}
